import React, { useEffect, useMemo, useState } from 'react';
import { PlaySquare, MessageSquarePlus } from 'lucide-react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton, Button, BrandName } from '../ui';
import { FeedbackDrawer } from '../features';
import { VoiceSelector } from './VoiceSelector';
import { auth } from '../../firebase';
import { authService } from '../../services/auth';
import { showToast } from '../../utils/toast';
import { useTheme } from '../../theme';

interface HeaderProps {
  showVideoSolutions?: boolean;
  onOpenVideoSolutions?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ showVideoSolutions = false, onOpenVideoSolutions }) => {
  const navigate = useNavigate();
  const { branding } = useTheme();
  const [user, setUser] = useState<User | null>(() => authService.getCurrentUser());
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, (u) => setUser(u));
  }, []);

  const userLabel = useMemo(() => {
    return user?.displayName || user?.email || 'User';
  }, [user]);

  const userInitial = useMemo(() => {
    const source = user?.displayName || user?.email || 'U';
    return source.trim().charAt(0).toUpperCase() || 'U';
  }, [user]);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate('/', { replace: true, state: { from: { pathname: '/home' }, reason: 'logout' } });
    } catch (error: any) {
      showToast.error(error?.message || 'Logout failed');
    }
  };

  return (
    <>
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-14">
            {/* Logo */}
            <div className="flex items-center gap-3">
              {branding.logoUrl && (
                <img src={branding.logoUrl} alt="Logo" className="w-9 h-9 rounded-xl object-contain" />
              )}
              <div>
                <h1 className="text-lg font-bold" style={{ color: branding.textColor }}>
                  <BrandName text={branding.tenantName} />
                </h1>
                <p className="text-[10px] -mt-0.5" style={{ color: branding.textColorMuted }}>{branding.tagline}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {showVideoSolutions && (
                <button
                  type="button"
                  onClick={onOpenVideoSolutions}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  title="Video Solutions"
                >
                  <PlaySquare className="w-4 h-4" />
                  <span className="text-sm font-medium hidden sm:inline">Video Solutions</span>
                </button>
              )}
              {/* <IconButton
                icon={<Bell className="w-5 h-5" />}
                tooltip="Notifications"
              /> */}

              <Button
                variant="secondary"
                size="sm"
                icon={<MessageSquarePlus className="w-4 h-4" />}
                onClick={() => setIsFeedbackOpen(true)}
                className="hidden sm:inline-flex"
              >
                Feedback
              </Button>
              <VoiceSelector />
              <IconButton
                icon={<MessageSquarePlus className="w-5 h-5" />}
                tooltip="Send Feedback"
                onClick={() => setIsFeedbackOpen(true)}
                className="sm:hidden"
              />

              {user ? (
                <div className="flex items-center gap-1">
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    type="button"
                    title={userLabel}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm bg-blue-500"
                    >
                      {userInitial}
                    </div>
                    <span className="text-sm font-medium hidden md:inline max-w-[80px] truncate text-gray-900">
                      {userLabel}
                    </span>
                  </button>

                  <Button variant="ghost" size="sm" onClick={handleLogout} type="button">
                    Logout
                  </Button>
                </div>
              ) : (
                <Link to="/">
                  <Button variant="secondary" size="sm" type="button">
                    Sign in
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>
      <FeedbackDrawer
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
    </>
  );
};

export default Header;
